// SL.L3a (#2959, recon Q5; ruling D8) — the sprint settings card, below the
// Phase 4 "Development In Progress" card. It shows the project's limit on
// active sprints; the edit control renders if and only if the server lists
// edit_sprint_config (W1), so the web holds no role or Fast Track logic.
//
// A save re-reads the settings. A refusal shows the server's text (#3055).
// When more sprints are active than the limit (lowered below the active count,
// ruling D6), a note says when the next sprint can start. A failed read shows
// a quiet error and never breaks the dashboard; a read for a previous project
// never lands.
import { useEffect, useRef, useState, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import { hasAction } from '../../lib/sprintLoopActions';
import { fetchSprintConfig, putSprintConfig, type SprintConfig } from '../../lib/sprintConfigApi';
import { refusalText } from '../../lib/sprintActionsApi';

export interface SprintConfigCardProps {
  projectId: string;
  cardCls?: string;
  cardStyle?: CSSProperties;
  SectionHeader: ComponentType<{ color: string; children: ReactNode }>;
}

const MIN = 1;
const MAX = 5;
const text: CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 13 };

function overLimitNote(c: SprintConfig): string | null {
  if (c.activeSprints <= c.maxActiveSprints) return null;
  return `${c.activeSprints} sprints are active. A new sprint can start once fewer than ${c.maxActiveSprints} ${c.maxActiveSprints === 1 ? 'is' : 'are'} active.`;
}

export default function SprintConfigCard({ projectId, cardCls, cardStyle, SectionHeader }: SprintConfigCardProps) {
  const [config, setConfig] = useState<SprintConfig | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // The project on screen, for a save that lands after an await (the Board's rule).
  const projectRef = useRef(projectId);
  useEffect(() => { projectRef.current = projectId; }, [projectId]);

  // Another project starts empty. Adjusted during render, not in an effect.
  const [configFor, setConfigFor] = useState(projectId);
  if (configFor !== projectId) {
    setConfigFor(projectId);
    setConfig(null);
    setLoadFailed(false);
    setRefusal(null);
  }

  useEffect(() => {
    let ignore = false;
    fetchSprintConfig(projectId).then(
      (c) => { if (!ignore) { setConfig(c); setDraft(String(c.maxActiveSprints)); } },
      () => { if (!ignore) setLoadFailed(true); },
    );
    return () => { ignore = true; };
  }, [projectId]);

  async function save() {
    const pid = projectId;
    const live = () => projectRef.current === pid;
    setSaving(true);
    setRefusal(null);
    try {
      const saved = await putSprintConfig(pid, Number(draft));
      // Re-read after a save; the PUT's own answer stands in if the re-read fails.
      const c = await fetchSprintConfig(pid).catch(() => saved);
      if (live()) { setConfig(c); setDraft(String(c.maxActiveSprints)); }
    } catch (e) {
      if (live()) setRefusal(refusalText(e, "The sprint settings couldn't be saved."));
    } finally {
      setSaving(false);
    }
  }

  if (!config && !loadFailed) return null;
  const note = config && overLimitNote(config);

  return (
    <div className={cardCls} style={cardStyle} data-testid="sprint-config-card">
      <SectionHeader color="var(--success)">Sprint settings</SectionHeader>
      {loadFailed || !config ? (
        <p style={{ ...text, color: 'var(--text-secondary)' }}>{"The sprint settings couldn't be loaded."}</p>
      ) : (
        <>
          <p style={{ ...text, color: 'var(--text)' }}>
            {`Maximum active sprints: ${config.maxActiveSprints}${config.isDefault ? ' (default)' : ''}`}
          </p>
          {note && (
            <p data-testid="sprint-limit-note" className="mt-1" style={{ ...text, fontSize: 12, color: 'var(--text-secondary)' }}>{note}</p>
          )}
          {hasAction(config.allowedActions, 'edit_sprint_config') && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <input
                type="number" min={MIN} max={MAX} step={1} value={draft} aria-label="Maximum active sprints"
                onChange={(e) => setDraft(e.target.value)} disabled={saving}
                style={{ ...text, width: 64, padding: '4px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
              />
              <button
                type="button" onClick={() => void save()} disabled={saving}
                style={{ ...text, fontWeight: 600, padding: '4px 12px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--primary)', color: '#fff', cursor: saving ? 'wait' : 'pointer' }}
              >
                Save
              </button>
            </div>
          )}
          {refusal && <p role="alert" className="mt-2" style={{ ...text, fontSize: 12, color: 'var(--error)' }}>{refusal}</p>}
        </>
      )}
    </div>
  );
}
